import React, {useEffect, useState} from "react";
import withStoreAuth from "../../../auth/StoreRouteAuth";
import StoreLayout from "./StoreLayout";
import {Btn, Notice, Panel} from "./StoreUI";
import {storeGet} from "../../../services/storeApi";
import {TbDownload, TbCopy, TbCheck} from "react-icons/tb";
import {API_DOC} from "./storeApiDocData";
import StoreButtonGuide from "./StoreButtonGuide";
import StoreDocsModal from "./StoreDocsModal";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사(쇼핑몰) API 명세서 · 연동가이드 · 버튼 가이드라인
 * ════════════════════════════════════════════════════════════════════════════════
 *  경로 : /store/api/docs
 *
 *  [탭 구성]
 *   1) 서비스 안내    : API 서비스내용 / 주체별 절차
 *   2) 연동 가이드    : 버튼 삽입 절차(스크립트 1줄) + 옵션
 *   3) 주문연동 API   : POST /api/open/v1/order-link 명세
 *   4) 배송료조회 API : GET /api/shipinfo/checkShippingFee 명세
 *   5) 버튼 가이드라인 : 색상/모양별 버튼 샘플 + 사용규칙
 *
 *  [문서 내려받기]
 *   [자료 받기] 버튼 -> StoreDocsModal 에서 연동 가이드(PDF) / 상품 소개서(PPTX) 선택.
 *   파일은 빌드할 때 scripts/buildStoreDocs.mjs 가 미리 만들어 public/docs 에 둔다.
 *   (예전의 'HTML 다운로드'·'PDF로 저장' 은 실제로 저장되지 않아 걷어냈다.
 *    자세한 경위는 StoreDocsModal.js 머리말 참고)
 *
 *  ※ 하위 표시부품(Section/Code/Table/Bullets)은 반드시 컴포넌트 '바깥'에 둔다.
 *    안에 두면 렌더마다 새 타입이 만들어져 불필요한 리마운트가 생긴다.
 * ════════════════════════════════════════════════════════════════════════════════
 */

const Section = ({title, desc, children}) => (
    <section className="tw-mb-9 last:tw-mb-0">
        <h3 className="st-display tw-text-[16.5px] tw-text-[color:var(--st-text)]">{title}</h3>
        {desc ? (
            <p className="tw-mb-3.5 tw-mt-1.5 tw-text-[12.5px] tw-leading-[1.8] tw-text-[color:var(--st-text-faint)] tw-break-keep">
                {desc}
            </p>
        ) : <div className="tw-h-3"/>}
        {children}
    </section>
);

const Code = ({code, id, copied, onCopy}) => (
    <div className="tw-relative">
        <pre className="st-mono st-scroll tw-overflow-x-auto tw-rounded-[16px] tw-border tw-px-4 tw-py-4 tw-text-[12px] tw-leading-[1.8] tw-text-[color:var(--st-text)]"
             style={{background: 'var(--st-field)', borderColor: 'var(--st-glass-line)', whiteSpace: 'pre'}}>
            {code}
        </pre>
        <button type="button" onClick={onCopy}
                className="st-btn st-btn-glass st-btn-sm tw-absolute tw-right-3 tw-top-3">
            {copied === id ? <><TbCheck/> 복사됨</> : <><TbCopy/> 복사</>}
        </button>
    </div>
);

const Table = ({head, rows}) => (
    <div className="st-scroll tw-overflow-x-auto">
        <table className="tw-w-full tw-min-w-[620px]">
            <thead>
            <tr>
                {head.map(h => (
                    <th key={h}
                        className="tw-whitespace-nowrap tw-border-b tw-px-3 tw-py-2.5 tw-text-left tw-text-[11.5px] tw-font-semibold tw-text-[color:var(--st-text-faint)]"
                        style={{borderColor: 'var(--st-glass-line)'}}>{h}</th>
                ))}
            </tr>
            </thead>
            <tbody>
            {rows.map((row, i) => (
                <tr key={i} className="tw-transition-colors hover:tw-bg-[rgba(203,182,255,.10)]">
                    {row.map((cell, j) => (
                        <td key={j}
                            className={`tw-border-b tw-px-3 tw-py-2.5 tw-text-[12.5px] tw-leading-[1.7] tw-break-keep ${
                                j === 0
                                    ? 'st-mono tw-whitespace-nowrap tw-text-[color:var(--st-text)]'
                                    : 'tw-text-[color:var(--st-text-sub)]'}`}
                            style={{borderColor: 'var(--st-glass-line)'}}>{cell}</td>
                    ))}
                </tr>
            ))}
            </tbody>
        </table>
    </div>
);

const Bullets = ({items}) => (
    <ul className="tw-space-y-2">
        {items.map((it, i) => (
            <li key={i} className="tw-flex tw-gap-2.5 tw-text-[13px] tw-leading-[1.8] tw-text-[color:var(--st-text-sub)] tw-break-keep">
                <span aria-hidden="true"
                      className="tw-mt-[9px] tw-h-1.5 tw-w-1.5 tw-flex-shrink-0 tw-rounded-full"
                      style={{background: 'linear-gradient(135deg,#FFD79A,#FFBE48)'}}/>
                <span>{it}</span>
            </li>
        ))}
    </ul>
);

/**
 * 웹 / 앱 서브탭
 *  같은 API 라도 웹(스크립트)과 앱(서버 경유)에서 해야 할 일이 달라서
 *  탭에 들어오자마자 자기 환경만 골라 볼 수 있게 한다.
 */
const PlatformNote = ({value, onChange, guide}) => (
    <div className="tw-mb-7">
        <div role="tablist" aria-label="연동 환경" className="st-tabs st-tabs-sm tw-mb-4">
            {[{key: 'web', label: '웹 (쇼핑몰 웹페이지)'}, {key: 'app', label: '앱 (iOS · Android)'}].map(t => (
                <button key={t.key} type="button" role="tab"
                        aria-selected={value === t.key}
                        onClick={() => onChange(t.key)}
                        className="st-tab">
                    {t.label}
                </button>
            ))}
        </div>
        <Bullets items={guide[value]}/>
    </div>
);

function StoreApiDocsPg() {

    const [tab, setTab] = useState('service');
    const [apiKey, setApiKey] = useState('');
    const [copied, setCopied] = useState('');
    /** 자료 받기 모달 */
    const [docsOpen, setDocsOpen] = useState(false);
    /** 연동가이드 / 주문연동 / 배송료조회 각 탭의 웹·앱 선택 (탭마다 따로 기억한다) */
    const [platform, setPlatform] = useState({guide: 'web', order: 'web', ship: 'web'});
    const pickPlatform = (tabKey) => (v) => setPlatform(prev => ({...prev, [tabKey]: v}));

    useEffect(() => {
        storeGet('/store/myInfo')
            .then(res => setApiKey(res?.data?.apiKey || ''))
            .catch(() => setApiKey(''));
    }, []);

    /** 예제코드에 실제 발급키를 끼워넣되, 미발급이면 자리표시자를 유지 */
    const withKey = (text) => String(text).replace(/\{\{API_KEY\}\}/g, apiKey || 'YOUR_API_KEY');

    const copy = async (text, id) => {
        try {
            await navigator.clipboard.writeText(withKey(text));
            setCopied(id);
            setTimeout(() => setCopied(''), 1600);
        } catch (e) { /* 무시 */ }
    };

    const TABS = [
        {key: 'service', label: '서비스 안내'},
        {key: 'guide', label: '연동 가이드'},
        {key: 'order', label: '주문연동 API'},
        {key: 'ship', label: '배송료조회 API'},
        {key: 'button', label: '버튼 가이드라인'},
    ];

    const codeProps = (id, code) => ({
        id, code: withKey(code), copied, onCopy: () => copy(code, id),
    });

    return (
        <StoreLayout active="apidoc"
                     eyebrow="Documentation"
                     title="API 명세서 · 연동가이드"
                     desc="비토리지 구매대행 API 연동에 필요한 모든 내용을 담았습니다">

            {/*═══ 문서 내려받기 ═══*/}
            <Panel className="st-rise st-rise-3 tw-mb-4 tw-p-4">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <p className="tw-min-w-[140px] tw-flex-1 tw-text-[12.5px] tw-text-[color:var(--st-text-sub)]">
                        연동 가이드(PDF)와 상품 소개서(PPTX)를 내려받아 개발자분께 전달하실 수 있습니다
                    </p>
                    <Btn size="sm" tone="dawn" onClick={() => setDocsOpen(true)}>
                        <TbDownload/> 자료 받기
                    </Btn>
                </div>
            </Panel>

            <StoreDocsModal open={docsOpen} onClose={() => setDocsOpen(false)}/>

            {/*═══ 탭 — 세그먼트 컨트롤 (칸마다 테두리를 두르지 않는다) ═══*/}
            <div role="tablist" aria-label="문서 탭"
                 className="st-tabs st-rise st-rise-3 tw-mb-6">
                {TABS.map(t => (
                    <button key={t.key} type="button" role="tab"
                            aria-selected={tab === t.key}
                            onClick={() => setTab(t.key)}
                            className="st-tab">
                        {t.label}
                    </button>
                ))}
            </div>

            <Panel className="st-rise st-rise-4 tw-p-6 sm:tw-p-7">

                {/*═══ 1. 서비스 안내 ═══*/}
                {tab === 'service' ? (
                    <>
                        <Section title="API 서비스내용">
                            <Bullets items={API_DOC.serviceIntro}/>
                        </Section>

                        <Section title="비토리지 서비스 — 배송사"
                                 desc="아래 배송사별로 기간과 요금이 비교 견적되며, 고객이 원하는 배송사를 실시간으로 선택해 결제합니다">
                            <Table head={['배송사', '평균 소요기간']} rows={API_DOC.couriers}/>
                        </Section>

                        <Section title="비토리지 서비스 — 결제수단"
                                 desc="거의 대부분의 국가 현지인들이 불편함이나 제한 없이 결제할 수 있습니다">
                            <Bullets items={API_DOC.payMethods}/>
                        </Section>

                        <Section title="비토리지 서비스 — 실시간 문의응대">
                            <Bullets items={API_DOC.inquiryChannels}/>
                        </Section>

                        <Section title="비토리지 서비스 — 그 외">
                            <Bullets items={API_DOC.etcServices}/>
                        </Section>

                        <Section title="한 눈에 보는 주체별 절차">
                            <div className="tw-space-y-2.5">
                                {API_DOC.processes.map(p => (
                                    <div key={p.who} className="tw-rounded-[16px] tw-border tw-px-4 tw-py-3.5"
                                         style={{background: 'rgba(203,182,255,.10)', borderColor: 'var(--st-glass-line)'}}>
                                        <p className="tw-mb-2 tw-text-[13px] tw-font-bold tw-text-[#B8790F]">{p.who}</p>
                                        <p className="tw-text-[12.5px] tw-leading-[1.9] tw-text-[color:var(--st-text-sub)] tw-break-keep">
                                            {p.flow}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    </>
                ) : null}

                {/*═══ 2. 연동 가이드 ═══*/}
                {tab === 'guide' ? (
                    <>
                        <PlatformNote value={platform.guide} onChange={pickPlatform('guide')}
                                      guide={API_DOC.platformGuide.guide}/>

                        <Section title="STEP 1. API 키 발급"
                                 desc="대시보드 > API 키 에서 [API 키 발급] 을 누르세요. 발급 후 비토리지가 활성화하면 바로 사용하실 수 있습니다.">
                            {apiKey
                                ? <Notice tone="info">
                                    내 API 키 <b className="st-mono tw-break-all tw-text-[color:var(--st-text)]">{apiKey}</b>
                                </Notice>
                                : <Notice tone="warn">아직 발급된 키가 없습니다. 대시보드에서 먼저 발급해주세요.</Notice>}
                        </Section>

                        <Section title={API_DOC.keySecurity.title}
                                 desc={platform.guide === 'web'
                                     ? '웹은 키가 페이지 소스에 노출됩니다. 어디까지 허용되는지 확인해주세요.'
                                     : '앱은 키를 절대 앱 안에 두면 안 됩니다. 연동 전에 꼭 확인해주세요.'}>
                            <Notice tone="warn">
                                {platform.guide === 'web' ? (
                                    <>
                                        {API_DOC.keySecurity.web}
                                        <br/><br/>
                                        <b>자사 URL 확인</b> — {API_DOC.keySecurity.webDomain}
                                    </>
                                ) : (
                                    <>
                                        {API_DOC.keySecurity.app}
                                        <br/><br/>
                                        <b>연동 순서</b> — {API_DOC.keySecurity.flow}
                                        <br/><br/>
                                        <b>redirectUrl 이란</b> — {API_DOC.keySecurity.flowNote}
                                    </>
                                )}
                                <br/><br/>
                                <b>유출 시</b> — {API_DOC.keySecurity.leak}
                            </Notice>
                        </Section>

                        {platform.guide === 'web' ? (
                            <>
                                <Section title="STEP 2. 스크립트 1줄 삽입"
                                         desc="상품 상세페이지와 장바구니 페이지의 </body> 직전에 아래 한 줄만 붙여넣으면 끝입니다. 별도 개발 없이 버튼이 자동으로 생성됩니다.">
                                    <Code {...codeProps('script', API_DOC.snippet.script)}/>
                                </Section>

                                <Section title="STEP 3. 상품정보 지정"
                                         desc="버튼이 어떤 상품을 연동할지 알 수 있도록 상품영역에 data- 속성만 지정해주세요. (장바구니는 여러 개 반복하면 됩니다)">
                                    <Code {...codeProps('dataset', API_DOC.snippet.dataset)}/>
                                    <div className="tw-h-3"/>
                                    <Table head={['속성', '필수', '설명']} rows={API_DOC.datasetSpec}/>
                                </Section>

                                <Section title="STEP 4. 버튼 위치 지정 (선택)"
                                         desc="버튼을 원하는 자리에 넣고 싶다면 아래 요소를 배치하세요. 지정하지 않으면 상품영역 하단에 자동으로 생성됩니다.">
                                    <Code {...codeProps('mount', API_DOC.snippet.mount)}/>
                                </Section>

                                <Section title="스크립트 대신 직접 호출하기 (선택)"
                                         desc="자체 프론트엔드나 서버에서 직접 호출하셔도 됩니다. 응답의 redirectUrl 로 고객을 이동시키면 됩니다.">
                                    <p className="st-eyebrow tw-mb-2">JavaScript (fetch)</p>
                                    <Code {...codeProps('manual', API_DOC.snippet.manual)}/>
                                    <p className="st-eyebrow tw-mb-2 tw-mt-5">curl — 언어 무관 / 서버사이드</p>
                                    <Code {...codeProps('manualCurl', API_DOC.snippet.manualCurl)}/>
                                </Section>
                            </>
                        ) : (
                            <>
                                <Section title="STEP 2. [Delivery] 버튼을 앱 화면에 직접 만들기"
                                         desc="앱에는 스크립트를 넣을 수 없어 버튼을 직접 구현하셔야 합니다. 모양·문구·색상은 [버튼 가이드라인] 탭의 규격을 그대로 따라주세요.">
                                    <Notice tone="info">
                                        상품 상세화면에는 상품 1건, 장바구니 화면에는 담긴 상품 전체를 모아
                                        다음 단계의 <b>products 배열</b>로 넘기시면 됩니다. (최대 50건)
                                        <br/><br/>
                                        웹과 달리 화면에서 상품정보를 긁어올 필요가 없습니다.
                                        앱이 이미 가지고 있는 상품 데이터를 그대로 쓰시면 됩니다.
                                    </Notice>
                                </Section>

                                <Section title="STEP 3. 자사 서버에서 주문연동 API 호출"
                                         desc="앱이 아니라 자사 서버가 호출해야 합니다. 앱에 API 키를 넣으면 디컴파일로 추출됩니다.">
                                    <Code {...codeProps('appServer', API_DOC.snippet.appServer)}/>
                                    <div className="tw-h-3"/>
                                    <Table head={['필드', '타입', '필수', '설명']} rows={API_DOC.orderApi.request}/>
                                </Section>

                                <Section title="STEP 4. 비토리지로 이동"
                                         desc="딥링크를 먼저 시도하고, 실패하면 redirectUrl 을 외부 브라우저로 엽니다. 이후 작성·주문·결제는 전부 비토리지에서 끝나며 쇼핑몰로 되돌아오지 않습니다.">
                                    <Code {...codeProps('appMove', API_DOC.snippet.appMove)}/>
                                </Section>
                            </>
                        )}

                        <Section title="연동 점검">
                            <Code {...codeProps('health', API_DOC.snippet.health)}/>
                        </Section>
                    </>
                ) : null}

                {/*═══ 3. 주문연동 API ═══*/}
                {tab === 'order' ? (
                    <>
                        <PlatformNote value={platform.order} onChange={pickPlatform('order')}
                                      guide={API_DOC.platformGuide.order}/>

                        <Section title="POST /api/open/v1/order-link"
                                 desc="쇼핑몰 상품정보를 비토리지 주문서로 연동하고, 고객을 이동시킬 진입 URL을 발급받습니다.">
                            <Table head={['구분', '내용']} rows={API_DOC.orderApi.basic}/>
                        </Section>

                        <Section title="요청 헤더">
                            <Table head={['헤더', '필수', '설명']} rows={API_DOC.orderApi.headers}/>
                        </Section>

                        <Section title="요청 본문">
                            <Table head={['필드', '타입', '필수', '설명']} rows={API_DOC.orderApi.request}/>
                        </Section>

                        <Section title="요청 예시">
                            <p className="st-eyebrow tw-mb-2">HTTP</p>
                            <Code {...codeProps('orderReq', API_DOC.orderApi.reqExample)}/>
                            <p className="st-eyebrow tw-mb-2 tw-mt-5">curl</p>
                            <Code {...codeProps('orderReqCurl', API_DOC.orderApi.reqCurl)}/>
                        </Section>

                        <Section title="응답 (성공)">
                            <Code {...codeProps('orderRes', API_DOC.orderApi.resExample)}/>
                        </Section>

                        <Section title="응답 (실패)" desc="모든 실패응답은 동일한 규격을 사용합니다.">
                            <Code {...codeProps('orderErr', API_DOC.errorExample)}/>
                        </Section>

                        <Section title="에러코드">
                            <Table head={['code', 'HTTP', 'message', '설명']} rows={API_DOC.errorCodes}/>
                        </Section>
                    </>
                ) : null}

                {/*═══ 4. 배송료조회 API ═══*/}
                {tab === 'ship' ? (
                    <>
                        <PlatformNote value={platform.ship} onChange={pickPlatform('ship')}
                                      guide={API_DOC.platformGuide.ship}/>

                        <Section title="GET /api/shipinfo/checkShippingFee"
                                 desc="국가코드와 부피·무게만 전달하면 배송사별 최종 비용까지 계산되어 돌아옵니다. 별도 계산이 필요 없습니다.">
                            <Table head={['구분', '내용']} rows={API_DOC.shipApi.basic}/>
                        </Section>

                        <Section title="요청 파라미터">
                            <Table head={['파라미터', '타입', '필수', '설명']} rows={API_DOC.shipApi.request}/>
                        </Section>

                        <Section title="요청 예시">
                            <Code {...codeProps('shipReq', API_DOC.shipApi.reqExample)}/>
                        </Section>

                        <Section title="응답 필드" desc="배송사별로 아래 항목이 반환됩니다.">
                            <Table head={['필드', '설명']} rows={API_DOC.shipApi.responseFields}/>
                        </Section>

                        <Section title="응답 상태값" desc="금액 대신 아래 문자열이 반환될 수 있습니다.">
                            <Table head={['값', '의미', '발생조건']} rows={API_DOC.shipApi.statusValues}/>
                        </Section>

                        <Section title="응답 예시">
                            <Code {...codeProps('shipRes', API_DOC.shipApi.resExample)}/>
                        </Section>

                        <Section title="에러코드">
                            <Table head={['code', 'HTTP', 'message', '설명']} rows={API_DOC.errorCodes}/>
                        </Section>
                    </>
                ) : null}

                {/*═══ 5. 버튼 가이드라인 ═══*/}
                {tab === 'button' ? <StoreButtonGuide apiKey={apiKey}/> : null}
            </Panel>
        </StoreLayout>
    );
}

export default withStoreAuth(StoreApiDocsPg);
